import { motion } from 'framer-motion'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import { JSONTree } from 'react-json-tree'

import { LockClosedIcon } from '@heroicons/react/16/solid'
import {
  ArrowSmallRightIcon,
  StarIcon as StarIconOutline
} from '@heroicons/react/24/outline'
import {
  IdentificationIcon,
  StarIcon as StarIconSolid,
  UserIcon
} from '@heroicons/react/24/solid'
import { format } from 'date-fns'
import { colorByString } from '.'
import IndeterminateCheckbox from '../components/react-table/indeterminate-checkbox'
import {
  ASSESSMENT_SPACE_STATES,
  SMART_PREDICT_SPACE_STATES
} from '../constants/space-states'
import { query } from '../graphql'
import {
  filter,
  filterAdditionalData,
  filterMore,
  filterScore,
  filterStatus
} from './filters'
import { openPdfLoadingWindow } from './pdf-loading'
import { sortByCreatedStatus, sortByDate, sortByTtl } from './sortings'
import { getPrimaryTalentStatus } from './spaces-table-data'
import { TABLE_IDS, customizableMeta } from './table-column-settings'
import {
  getTalentStatusText,
  resolveLocalized,
  toStatusOption
} from './talent-statuses'
import {
  TALENT_STORAGE_COLUMN,
  TALENT_STORAGE_VALUES,
  countLockedTalents,
  isStorageLocked,
  isStoragePending,
  talentStorageValueOf
} from './talent-storage-table'

import { isNumber } from 'lodash'
import NativeTooltip from '../components/native-tooltip'
import SampleBadge from '../components/sample-data/sample-badge'
import { MATCHINGPROFILE } from '../constants/evaluation-types'
import trackEvents from '../constants/track-events'
import { trackEvent } from './analytics'
import { isSampleSpace } from './sample-data'
import { getScoreColorV1 } from './score-color'

const getName = ({ firstname, lastname }) => {
  if (!firstname && !lastname) return null
  if (!firstname) return lastname
  if (!lastname) return firstname

  return `${firstname} ${lastname}`
}

const COLUMNS = ({ t, careers_map, data, language, locale, actions }) => ({
  SELECT: {
    id: 'select',
    enableHiding: false,
    disableOnClick: true,
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          id: 'react-table-select-all',
          checked: table.getIsAllRowsSelected(),
          // indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler()
        }}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        checked={row.getIsSelected()}
        // disabled={!row.getCanSelect()}
        // indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    )
  },
  FAVORED_BY_USER: {
    accessorKey: 'favored_by',
    enableHiding: false,
    sortingFn: (rowA, rowB) =>
      Number(rowA.original.favored_by.isFavored) -
      Number(rowB.original.favored_by.isFavored),
    header: null,
    cell: ({ getValue, cell }) => {
      const { isFavored, action } = getValue()

      const tooltip_content = t('favored_space.tooltip')

      return (
        <motion.div
          // Beim Hover über die klebende Kopfzeile (z-[1]), sonst verdeckt sie
          // den Tooltip in der obersten Zeile
          className='inline-flex hover:relative hover:z-[2]'
          whileHover={{ scale: 1.1 }}
          // whileTap={isFavored ? undefined : { scale: 1.2, rotate: 45 }}
        >
          {isFavored ? (
            <button onClick={() => actions[action](cell.row.original)}>
              <StarIconSolid className='h-6 w-6 cursor-pointer text-amber-400' />
            </button>
          ) : (
            <NativeTooltip className='' text={tooltip_content} delay={1000 * 3}>
              <button onClick={() => actions[action](cell.row.original)}>
                <StarIconOutline className='h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-600' />
              </button>
            </NativeTooltip>
          )}
        </motion.div>
      )
    },
    disableOnClick: true
  },
  ADMIN_INSIGHTS: {
    id: 'admin-insights',
    accessorKey: 'id',
    enableSorting: false,
    enableHiding: false,
    disableOnClick: true,
    header: <span className='text-sm font-semibold text-gray-900'>ID</span>,
    cell: ({ getValue, row }) => (
      <>
        <span className='block text-sm font-medium text-gray-900'>
          {getValue()}
        </span>
        <JSONTree data={row.original} shouldExpandNodeInitially={() => false} />
      </>
    )
  },
  IDENTIFICATION: {
    accessorKey: 'identification',
    accessorFn: (row) => row.identification || row.email,
    enableSorting: false,
    enableHiding: false,
    meta: {
      label: t('talents.column_label_identification'),
      primary: true
    },
    header: (
      <span className='text-sm font-semibold text-gray-900'>
        {t('talents.column_header_identification')}
      </span>
    ),
    cell: ({ getValue, cell }) => {
      const identification = getValue()
      const { person, via, partner_user_id } = cell.row.original

      const name = getName({
        firstname: person?.firstname || person?.invitation_firstname,
        lastname: person?.lastname || person?.invitation_lastname
      })

      return (
        <div>
          <p className='truncate text-sm font-medium text-blue-700'>
            {/* Gesperrt im Talentspeicher. relative fängt das sr-only ein,
                sonst verlängert es im Scrollcontainer das Dokument. */}
            {isStorageLocked(cell.row.original) &&
              !isStoragePending(cell.row.original) && (
                <span className='relative mr-1.5 inline-flex align-text-bottom'>
                  <LockClosedIcon
                    aria-hidden='true'
                    className='size-4 text-gray-400'
                  />
                  <span className='sr-only'>
                    {t('pricing.talent_locked.row_sr')}
                  </span>
                </span>
              )}
            {via === 'EMAIL' ? identification : t('invitation_via_link')}
            {isSampleSpace(cell.row.original) && (
              <SampleBadge kind='talent' className='ml-2' />
            )}
          </p>

          {(!!name || partner_user_id) && (
            <div className='mt-2 flex items-center gap-x-2'>
              {!!name && (
                <p
                  className='flex items-center text-sm text-gray-500'
                  title='Talent Name'
                >
                  <UserIcon
                    className='mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400'
                    aria-hidden='true'
                  />
                  <span className='truncate'>{name}</span>
                </p>
              )}
              {partner_user_id && (
                <p
                  className='flex items-center text-sm text-gray-500'
                  title='Talent ID'
                >
                  <IdentificationIcon
                    className='mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400'
                    aria-hidden='true'
                  />
                  <span className='truncate'>{partner_user_id}</span>
                </p>
              )}
            </div>
          )}
        </div>
      )
    }
  },
  PARTNER_USER_ID: {
    invisible: true,
    enableHiding: false,
    accessorKey: 'partner_user_id',
    accessorFn: (row) => row.partner_user_id || '-',
    header: null,
    cell: () => <div />
  },
  NAME: {
    invisible: true,
    enableHiding: false,
    accessorKey: 'name',
    accessorFn: (row) => row.name || '-',
    header: null,
    cell: () => <div />
  },
  SOURCE: {
    accessorKey: 'assessment_id',
    enableSorting: false,
    meta: customizableMeta(TABLE_IDS.TALENTS, 'assessment_id', t),
    header: (
      <span className='text-sm font-semibold text-gray-900'>
        {t('talents.column_header_career')}
      </span>
    ),
    cell: ({ getValue }) => {
      const { title, external_custom_id, archived } =
        careers_map.get(getValue()) || {}

      const career_title_with_archived = archived
        ? t('talents.career_archived_title', { career_title: title })
        : title

      const career_title = external_custom_id
        ? `${career_title_with_archived} | ${external_custom_id}`
        : career_title_with_archived

      return (
        <span
          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
            colorByString(career_title)?.backgroundColor
          } ${colorByString(career_title)?.text}`}
        >
          {career_title || (
            <span title={t('career_not_set_description')}>
              {t('career_not_set')}
            </span>
          )}
        </span>
      )
    },
    filter: {
      id: 'assessment_id',
      filterKey: 'computed',
      operator: 'in',
      fn: filter,
      name: t('talents.filter_title_sources'),
      column: 'assessment_id',
      options: [
        { id: 'ATTRACT', title: 'Attract' },
        { id: 'TALENTPOOL', title: t('talents.talentpool_title') },
        ...(data ? data.careers : [])
      ]
        .filter(({ archived }) => !archived)
        .map(({ id, title, external_custom_id }) => ({
          value: id,
          label: external_custom_id ? `${title} | ${external_custom_id}` : title
        }))
    }
  },
  UPDATED_AT: {
    accessorKey: 'updatedAt',
    sortDescFirst: true,
    sortingFn: sortByDate,
    meta: customizableMeta(TABLE_IDS.TALENTS, 'updatedAt', t),
    header: (
      <span className='text-sm font-semibold text-gray-900'>
        {t('talents.column_header_last_activity')}
      </span>
    ),
    cell: ({ getValue }) => (
      <span className='text-sm text-gray-500'>
        {t('talents.updated_at', {
          datetime: format(new Date(getValue()), 'dd.MM.yyyy | HH:mm', {
            locale: locale[language]
          })
        })}
      </span>
    )
  },
  CREATED_STATUS: {
    accessorKey: 'created_status',
    sortDescFirst: true,
    sortingFn: sortByCreatedStatus,
    meta: customizableMeta(TABLE_IDS.TALENTS, 'created_status', t),
    header: (
      <span className='text-sm font-semibold text-gray-900'>
        {t('talents.column_header_created_at')}
      </span>
    ),
    cell: ({ getValue }) => (
      <span className='text-sm text-gray-500'>
        {format(new Date(getValue().createdAt), 'dd.MM.yyyy', {
          locale: locale[language]
        })}
      </span>
    )
  },
  ID: {
    accessorKey: 'id',
    header: null,
    cell: () => <div />,
    invisible: true,
    enableHiding: false
  },
  OWNER: {
    accessorKey: 'owner',
    header: null,
    cell: () => <div />,
    invisible: true,
    enableHiding: false
  },
  MORE: {
    accessorKey: 'more',
    header: null,
    cell: () => <div />,
    invisible: true,
    enableHiding: false,
    filter: {
      id: 'more',
      filterKey: 'computed',
      operator: 'in',
      fn: filterMore,
      name: t('talents.filter_title_more'),
      column: 'more',
      options: [
        {
          label: t('talents.filter_value_6_month'),
          value: 'BEFORE_6_MONTH'
        },
        {
          label: t('talents.filter_value_favorites'),
          value: 'IS_FAVORED'
        }
      ]
    }
  },
  // Nur für den Filter, in der Liste nur mit gesperrten Talenten. Die Werte
  // sind keine Suchtreffer, sonst fände „locked" alle gesperrten Zeilen.
  TALENT_STORAGE: {
    id: TALENT_STORAGE_COLUMN,
    accessorFn: talentStorageValueOf,
    header: null,
    cell: () => <div />,
    invisible: true,
    enableHiding: false,
    enableSorting: false,
    enableGlobalFilter: false,
    filter: {
      id: TALENT_STORAGE_COLUMN,
      filterKey: 'computed',
      operator: 'in',
      fn: filter,
      name: t('pricing.talent_locked.filter_title'),
      column: TALENT_STORAGE_COLUMN,
      options: [
        {
          label: t('pricing.talent_locked.filter_visible'),
          value: TALENT_STORAGE_VALUES.VISIBLE
        },
        {
          label: t('pricing.talent_locked.filter_locked'),
          value: TALENT_STORAGE_VALUES.LOCKED
        }
      ]
    }
  },
  ADDITIONAL_DATA: {
    accessorKey: 'additional_requested_data',
    header: null,
    cell: () => <div />,
    invisible: true,
    enableHiding: false,
    filter: {
      id: 'additional_requested_data',
      filterKey: 'computed',
      operator: 'in',
      fn: filterAdditionalData,
      name: t('talents.filter_title_additional_data'),
      column: 'additional_requested_data',
      options: (() => {
        // Sammle alle zusätzlichen Daten aus allen Spaces
        const allAdditionalData = {}

        // Durchlaufe alle Spaces und sammle die zusätzlichen Daten
        data?.spaces?.forEach((space) => {
          try {
            const additionalData =
              typeof space.additional_requested_data === 'string'
                ? JSON.parse(space.additional_requested_data)
                : space.additional_requested_data

            if (!additionalData) return

            // Füge jedes Feld und seine möglichen Werte hinzu
            Object.entries(additionalData).forEach(([id, value]) => {
              if (!value.label) return
              if (value.type !== 'dropdown') return

              if (!allAdditionalData[id]) {
                allAdditionalData[id] = {
                  label: value.label,
                  values: new Set()
                }
              }

              // Verarbeite die Eingabewerte basierend auf dem Typ
              if (value.multiple && Array.isArray(value.input)) {
                // Bei Mehrfachauswahl: Füge alle ausgewählten Werte hinzu
                value.input.forEach((item) => {
                  if (item.value) {
                    allAdditionalData[id].values.add(item.value)
                  }
                })
              } else if (Array.isArray(value.input) && value.input.length > 0) {
                // Bei Einzelauswahl mit Array-Struktur: Füge den ersten Wert hinzu
                if (value.input[0].value) {
                  allAdditionalData[id].values.add(value.input[0].value)
                }
              } else if (
                value.input &&
                typeof value.input === 'object' &&
                value.input.value
              ) {
                // Für einzelne Dropdown-Werte
                allAdditionalData[id].values.add(value.input.value)
              } else if (value.input) {
                // Fallback für andere Formate
                allAdditionalData[id].values.add(value.input)
              }
            })
          } catch (e) {
            console.error(
              'Fehler beim Parsen von additional_requested_data:',
              e
            )
          }
        })

        // Konvertiere die gesammelten Daten in die Filteroptionen-Struktur
        const options = Object.entries(allAdditionalData).map(
          ([id, { label, values }]) => {
            return {
              label,
              options: [
                { value: id, label: t('talents.filter_all') },
                ...Array.from(values).map((value) => ({
                  value: `${id}:${value}`,
                  label: value.toString()
                }))
              ]
            }
          }
        )

        // Nur zurückgeben, wenn es tatsächlich Optionen gibt
        return options
      })()
    }
  }
})

/**
 * Zusatzfelder aus dem Einladungsformular (Felder der Stelle, Partner-Felder
 * für Talentpool und Attract, Berufserfahrung) als optionale Tabellenspalten.
 * Katalog und Werte kommen aus buildExportColumns, damit Tabelle und CSV-Export
 * dieselben Felder unter denselben Ids kennen. Datei-Felder bleiben draußen,
 * eine Dateianzahl hilft in der Tabelle nicht weiter.
 *
 * Optional heißt standardmäßig ausgeblendet, der Nutzer fügt sie unter Spalten
 * hinzu. Die globale Suche läuft nicht über diese Spalten, sonst würden
 * ausgeblendete Antworten Treffer erzeugen.
 *
 * Gesperrte Talente (Talentspeicher) haben keine Zusatzangaben in den Daten.
 * Der Wert bleibt leer, damit sie beim Sortieren hinten landen, die Zelle
 * zeigt den Platzhalter.
 */
export const createAdditionalDataColumns = ({ fields = [], ctx }) =>
  fields.map((field) => ({
    id: field.id,
    accessorFn: (space) =>
      isStorageLocked(space)
        ? undefined
        : (field.accessor(space, ctx) ?? undefined),
    sortUndefined: 1,
    enableGlobalFilter: false,
    meta: { label: field.label, customizable: true, optional: true },
    header: (
      <span className='block max-w-[16rem] truncate text-sm font-semibold text-gray-900'>
        {field.label}
      </span>
    ),
    cell: ({ getValue, row }) => {
      if (isStorageLocked(row?.original)) {
        return (
          <span className='text-sm text-gray-400'>
            {ctx?.t?.('pricing.talent_locked.placeholder')}
          </span>
        )
      }

      const value = getValue()

      if (value === undefined || value === '') {
        return <span className='text-sm text-gray-400'>-</span>
      }

      return (
        <span
          title={String(value)}
          className='block max-w-[16rem] truncate text-sm text-gray-500'
        >
          {value}
        </span>
      )
    }
  }))

export const createTalentTableColumns = ({
  adminInsights,
  t,
  careers_map,
  data,
  language,
  partner,
  locale,
  handleNavigate,
  actions,
  displayIntegrationFilterOptions,
  additionalColumns = []
}) => {
  const {
    SELECT,
    FAVORED_BY_USER,
    ADMIN_INSIGHTS,
    IDENTIFICATION,
    PARTNER_USER_ID,
    NAME,
    SOURCE,
    UPDATED_AT,
    CREATED_STATUS,
    ID,
    OWNER,
    MORE,
    TALENT_STORAGE,
    ADDITIONAL_DATA
  } = COLUMNS({ t, careers_map, data, language, locale, actions })

  // Filter „Talentspeicher" nur, wenn wirklich Talente gesperrt sind. In
  // legacy und preview tragen die Zeilen kein storage_locked.
  const hasLockedTalents = countLockedTalents(data?.spaces) > 0

  // Eigene Talent-Status des Partners als Untergruppe im Filter. Archivierte
  // nur, wenn noch ein Talent sie trägt, sonst füllt sich der Filter mit Ballast.
  const usedStatusIds = new Set(
    (data?.spaces || []).map((space) => space.smart_predict_status?.status?.id)
  )
  const customStatusOptions = (partner?.talentStatuses || [])
    .map((definition, index) => toStatusOption(definition, index))
    .filter((status) => !status.archived || usedStatusIds.has(status.id))
    .map((status) => ({
      value: status.filterValue,
      label: status.archived
        ? `${resolveLocalized(status.label, language)} (${t('common.archived')})`
        : resolveLocalized(status.label, language)
    }))
  const systemStatusOptions = Object.keys(SMART_PREDICT_SPACE_STATES)
    .map((key) => ({
      value: SMART_PREDICT_SPACE_STATES[key].filterValue,
      label: t(SMART_PREDICT_SPACE_STATES[key].filterValue),
      sortWeight: SMART_PREDICT_SPACE_STATES[key].sortWeight
    }))
    .sort((a, b) => (a.sortWeight < b.sortWeight ? -1 : 1))

  // Ohne eigene Status bleibt die flache Liste. Mit eigenen Status werden
  // beide Gruppen als statische Sektionen mit Überschrift gezeigt, damit
  // klar ist, welche Status von Aivy kommen und welche vom Team.
  const talentStatusFilterOptions = customStatusOptions.length
    ? [
        {
          label: t('talents.filter_group_system_statuses'),
          static: true,
          options: systemStatusOptions
        },
        {
          label: t('talents.filter_group_custom_statuses'),
          static: true,
          options: customStatusOptions
        }
      ]
    : systemStatusOptions

  return [
    SELECT,
    adminInsights && ADMIN_INSIGHTS,
    IDENTIFICATION,
    FAVORED_BY_USER,
    SOURCE,
    {
      accessorKey: 'progress',
      sortDescFirst: true,
      meta: customizableMeta(TABLE_IDS.TALENTS, 'progress', t),
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('talents.column_header_progress')}
        </span>
      ),
      cell: ({ getValue }) => {
        const progress = getValue() || 0

        return (
          <div style={{ height: 48, width: 48 }}>
            <CircularProgressbar
              strokeWidth={4}
              value={progress}
              text={`${progress}% `}
              className='font-semibold'
              styles={buildStyles({
                pathColor: '#29CCC7',
                textColor: '#29CCC7',
                trailColor: '#F5F7FA',
                textSize: '24px'
              })}
            />
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        const progressA = rowA.original.progress || 0
        const progressB = rowB.original.progress || 0

        return progressA < progressB ? 1 : progressA > progressB ? -1 : 0
      }
    },
    {
      accessorKey: 'status',
      sortDescFirst: true,
      disableOnClick: true,
      meta: customizableMeta(TABLE_IDS.TALENTS, 'status', t),
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('talents.column_header_status')}
        </span>
      ),
      cell: ({ cell }) => {
        const space = cell.row.original
        const { status, cta } = getPrimaryTalentStatus(space)
        const openStatusDrawer = () => actions.setSpaceForStateCTA(space)
        const ctaLabel = t('talents.actions.setSpaceForStateCTA')

        // Abgeschlossen ohne Talent-Status: die Aufforderung ersetzt das Label.
        if (cta === 'replace') {
          return (
            <span
              onClick={openStatusDrawer}
              className='cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-600'
            >
              {ctaLabel}
            </span>
          )
        }

        return (
          <div className='flex flex-col items-start gap-y-0.5'>
            <span
              onClick={() => handleNavigate(cell)}
              className='text-sm text-gray-500'
            >
              {getTalentStatusText({
                status,
                t,
                language,
                progress: space.progress
              })}
            </span>
            {/* Abgelaufen oder nicht zustellbar: das Assessment kommt von
                sich aus nicht weiter, ein Talent-Status lohnt sich trotzdem. */}
            {cta === 'append' && (
              <span
                onClick={openStatusDrawer}
                className='cursor-pointer text-xs font-medium text-blue-700 hover:text-blue-600'
              >
                {ctaLabel}
              </span>
            )}
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        // '291' < '3'
        const a_sortWeight =
          getPrimaryTalentStatus(rowA.original).status?.sortWeight || '9'
        const b_sortWeight =
          getPrimaryTalentStatus(rowB.original).status?.sortWeight || '9'

        return a_sortWeight < b_sortWeight
          ? 1
          : a_sortWeight > b_sortWeight
            ? -1
            : 0
      }
    },
    {
      invisible: true,
      enableHiding: false,
      accessorKey: 'assessment_status',
      header: null,
      cell: () => <div />,
      filter: {
        id: 'assessment_status',
        filterKey: 'computed',
        operator: 'in',
        fn: filterStatus,
        name: t('talents.filter_title_assessment_status'),
        column: 'assessment_status',
        options: Object.keys(ASSESSMENT_SPACE_STATES)
          .filter(
            displayIntegrationFilterOptions
              ? () => true // use all ASSESSMENT_SPACE_STATES
              : (k) => !k.includes('INTEGRATION_')
          )
          .map((key) => ({
            value: ASSESSMENT_SPACE_STATES[key].filterValue,
            label: t(ASSESSMENT_SPACE_STATES[key].filterValue),
            sortWeight: ASSESSMENT_SPACE_STATES[key].sortWeight
          }))
          .sort((a, b) => (a.sortWeight < b.sortWeight ? -1 : 1))
      }
    },
    {
      invisible: true,
      enableHiding: false,
      accessorKey: 'smart_predict_status',
      header: null,
      cell: () => <div />,
      filter: {
        id: 'smart_predict_status',
        filterKey: 'computed',
        operator: 'in',
        fn: filterStatus,
        name: t('talents.filter_title_smart_predict_status'),
        column: 'smart_predict_status',
        options: talentStatusFilterOptions
      }
    },
    {
      accessorKey: 'score',
      sortDescFirst: true,
      disableOnClick: true,
      meta: customizableMeta(TABLE_IDS.TALENTS, 'score', t),
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('talents.column_header_score')}
        </span>
      ),
      onClick: async (cell) => {
        const {
          owner,
          career_id,
          id: space_id,
          partner_user_id,
          matching_score_to_space_career: score
        } = cell.row.original

        // Gesperrt im Talentspeicher öffnet der Limit-Dialog statt des PDFs.
        // Vorläufig maskiert passiert nichts, der Report öffnet erst danach.
        if (isStoragePending(cell.row.original)) return
        if (isStorageLocked(cell.row.original)) {
          actions?.openTalentStorageDialog?.()
          return
        }

        if (!isNumber(score)) return

        const { diagnostic_version } = careers_map.get(career_id)
        const variables = { career_id, language }

        if (partner_user_id) variables.partner_user_id = partner_user_id
        else variables.user_id = owner

        if (diagnostic_version === 2) {
          variables.space_id = space_id
          variables.type = MATCHINGPROFILE
        }

        const pdfDocument = openPdfLoadingWindow({ t, language })

        try {
          const pdf = await query({ query: 'generatePdf', variables })
          pdfDocument.location.href = JSON.parse(pdf).headers.Location
          trackEvent(trackEvents.VIEW_MATCHING_PROFILE, { diagnostic_version })
        } catch (err) {
          console.error(err)
          pdfDocument.close()
        }
      },
      cell: ({ row }) => {
        const { career_id, matching_score_to_space_career: score } =
          row.original

        // Platzhalter ohne Zahl, der echte Score steht nicht in den Daten.
        // Der Klick landet über die Zelle bei onClick und öffnet den Dialog.
        if (isStoragePending(row.original)) {
          return (
            <span className='relative inline-flex h-8 w-16 animate-pulse rounded-full bg-gray-100'>
              <span className='sr-only'>
                {t('pricing.talent_locked.pending_sr')}
              </span>
            </span>
          )
        }

        if (isStorageLocked(row.original)) {
          return (
            <button
              type='button'
              aria-haspopup='dialog'
              aria-label={t('pricing.talent_locked.score_aria')}
              title={t('pricing.talent_locked.score_aria')}
              className='inline-flex h-8 w-16 items-center justify-center rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'
            >
              <LockClosedIcon
                aria-hidden='true'
                className='size-4 text-gray-500'
              />
            </button>
          )
        }

        if (!isNumber(score)) return null

        const rounded_score = Math.round(score)
        const career = careers_map.get(career_id)

        if (!career) {
          return (
            <span className='inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800'>
              {t('common.archived')}
            </span>
          )
        }

        const { diagnostic_version } = career
        const { isV1ScoreVisible, isV2ScoreVisible } = partner

        const displayScore =
          (diagnostic_version === 1 && isV1ScoreVisible) ||
          (diagnostic_version === 2 && isV2ScoreVisible)

        return (
          <div
            style={{
              height: 32,
              width: 64,
              // TODO:
              // we dont know the dimension_matches from matchMeIfYouCan here
              // must be fixed with list spaces by partner
              backgroundColor: getScoreColorV1(null)
            }}
            className='z-50 flex items-center justify-center gap-x-0.5 rounded-full hover:opacity-80'
          >
            {displayScore && (
              <span className='font-bold text-white'>{rounded_score}</span>
            )}

            <svg
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 20 20'
              fill='currentColor'
              className='size-4 text-white'
            >
              <path d='M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z' />
              <path d='M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z' />
            </svg>
          </div>
        )
      },
      filter: {
        id: 'score',
        filterKey: 'computed',
        operator: 'in',
        fn: filterScore,
        name: t('talents.filter_title_fit'),
        column: 'score',
        options: [
          {
            label: t('talents.filter_high_fit'),
            value: 'HIGH_FIT'
          },
          {
            label: t('talents.filter_middle_fit'),
            value: 'MIDDLE_FIT'
          },
          {
            label: t('talents.filter_low_fit'),
            value: 'LOW_FIT'
          }
        ]
      }
    },
    UPDATED_AT,
    CREATED_STATUS,
    ...additionalColumns,
    ID,
    OWNER,
    PARTNER_USER_ID,
    NAME,
    MORE,
    hasLockedTalents && TALENT_STORAGE,
    ADDITIONAL_DATA,
    {
      accessorKey: 'view',
      enableSorting: false,
      enableHiding: false,
      header: '',
      cell: () => (
        <ArrowSmallRightIcon className='ml-auto h-5 w-5 text-gray-500 group-hover:text-gray-900' />
      )
    }
  ]
    .filter((item) => item)
    .map((item) => {
      if (item.disableOnClick) return item

      return {
        ...item,
        onClick: handleNavigate
      }
    })
}

export const createRecoverTalentsTableColumns = ({
  adminInsights,
  t,
  careers_map,
  data,
  language,
  locale
}) => {
  const {
    SELECT,
    ADMIN_INSIGHTS,
    IDENTIFICATION,
    PARTNER_USER_ID,
    NAME,
    SOURCE,
    CREATED_STATUS,
    ID,
    OWNER
  } = COLUMNS({ t, careers_map, data, language, locale })

  return [
    SELECT,
    adminInsights && ADMIN_INSIGHTS,
    {
      ...IDENTIFICATION,
      cell: ({ getValue, row }) => (
        <span className='inline-flex items-center gap-x-2'>
          <span className='text-sm font-medium text-gray-900'>
            {getValue()}
          </span>
          {isSampleSpace(row.original) && <SampleBadge kind='talent' />}
        </span>
      )
    },
    PARTNER_USER_ID,
    NAME,
    SOURCE,
    {
      accessorKey: 'soft_deleted',
      sortDescFirst: true,
      sortingFn: sortByDate,
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('talents.column_header_soft_deleted')}
        </span>
      ),
      cell: ({ getValue }) => (
        <span className='text-sm text-gray-500'>
          {t('talents.soft_deleted', {
            datetime: format(new Date(getValue()), 'dd.MM.yyyy | HH:mm', {
              locale: locale[language]
            })
          })}
        </span>
      )
    },
    {
      accessorKey: 'cron_ttl',
      sortingFn: sortByTtl,
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('talents.column_header_ttl')}
        </span>
      ),
      cell: ({ getValue }) => {
        const { ttl } = getValue() || {}

        if (!ttl) return null

        return (
          <span className='text-sm text-gray-500'>
            {t('talents.ttl', {
              datetime: format(new Date(ttl * 1000), 'dd.MM.yyyy | HH:mm', {
                locale: locale[language]
              })
            })}
          </span>
        )
      }
    },
    { ...CREATED_STATUS, filter: undefined },
    ID,
    OWNER
  ].filter((item) => item)
}
